import { useState, Dispatch, SetStateAction, useCallback } from 'react';
import { AbTest } from '../../../models/AbTest';
import { useAbTestingApi } from '../../../api/useAbTestingApi';
import { IAbTestActions } from '../../../models/AbTestActions';
import { AbTestType } from '../../../models/AbTestType';
import { useAppContext } from '@app/react-common/AppContext';
import { AxiosError } from 'react-axios';
import { ApiErrorNotification } from '@app/react-common/models/ApiErrorNotification';
import { TimeUnits } from '@utils/times-utils';
import { AbTestWinnerRequestDto } from '../../../models/AbTestWinnerRequestDto';
import { SegmentMetric } from '../AbTestCard/AbTestCard.types';
import { HttpStatusCode } from '@pdqdev/pdq-shared/dist/utils/http-status-codes';
import { AbTestTypeCategory, AbTestWidgetTypeLabels } from '../../../models/AbTestCreationType';
import { normalizeAbTestDataArray } from '../../../utils/normalizeAbTestData';

// Site-wide experiments that must not overlap — only one may run at a time.
const MUTUALLY_EXCLUSIVE_TEST_TYPES = [
  AbTestTypeCategory.CROSS_SITE,
  AbTestTypeCategory.PRODUCT_PAGE,
  AbTestTypeCategory.SMART_CART,
];

const useAbTests = () => {
  const [draftAbTests, setDraftAbTests] = useState<AbTest[]>([]);
  const [segmentMetrics, setSegmentMetric] = useState<SegmentMetric[]>([]);
  const [testVariantMetrics, setTestVariantMetrics] = useState<any[]>([]);
  const [totalAnnualizedLift, setTotalAnnualizedLift] = useState<number | null>(null);

  const [runningAbTests, setRunningAbTests] = useState<AbTest[]>([]);
  const [concludedAbTests, setConcludedAbTests] = useState<AbTest[]>([]);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState<number | null>(null);
  const [bigSpenderThreshold, setBigSpenderThreshold] = useState<number | null>(null);
  const { showFullPageLoader } = useAppContext();
  const [apiErrorNotification, setApiErrorNotification] = useState<ApiErrorNotification>(null);

  const failureHandler = useCallback(async (response, status) => {
    if (status == 422) {
      setApiErrorNotification({ content: response.message, title: getErrorTitle(response.message) });
    } else {
      console.error('Response error:', response.message, status);
      showToast({ message: `An error has ocurred. Please contact support if the problem persists.`, mode: 'danger' });
    }
    showFullPageLoader(false);
  }, []);

  const abTestingApi = useAbTestingApi(true, failureHandler);
  const noIntercepteroAbTestingApi = useAbTestingApi(false);
  const { showToast } = useAppContext();

  const fetchTests = async (type: AbTestType, setTests: Dispatch<SetStateAction<AbTest[]>>) => {
    try {
      const response = await abTestingApi.get(`/ab-tests?filter=${type}&includeVariants=true`);
      const abTests = response.data as AbTest[];
      if (response?.data?.length > 0 && Array.isArray(response.data[0].abTestRules)) {
        showToast({
          message: 'AB Test Maintenance in progress. Please check back in a few minutes.',
          mode: 'warning',
          duration: TimeUnits._SECOND_IN_MS * 10,
        });
        return;
      }
      // Normalize externalId fields from string to number (handles BIGINT serialization)
      const normalizedAbTests = normalizeAbTestDataArray(abTests);
      setTests(
        normalizedAbTests.sort((a, b) => {
          return b.id - a.id;
        }),
      );
    } catch (error) {
      console.error('Error fetching AbTests:', error);
    }
  };

  // Function that manages state internally instead of returning promises
  const fetchSegmentsOrTestVariantMetrics = async (
    id: number,
    withVariant: boolean,
    testTypeCategory?: AbTestTypeCategory,
  ): Promise<void> => {
    try {
      const response = await abTestingApi.get(
        `/ab-tests/segments-metrics/${id}?testTypeCategory=${testTypeCategory || AbTestTypeCategory.SHIPPING_RATES}&withVariant=${withVariant}`,
      );
      const metricsData = response.data;

      if (withVariant) {
        // Update test variant metrics state
        setTestVariantMetrics(metricsData);
      } else {
        // Update segment metrics state
        setSegmentMetric(metricsData);
      }
    } catch (error) {
      console.error('Error fetching SegmentMetric or TestVariantMetrics:', error);
      throw error; // Re-throw so caller can handle it
    }
  };

  // Function that manages state internally instead of returning promises
  const fetchTotalAnnualizedLift = async (
    id: number,
    segments: string[],
    testTypeCategory?: AbTestTypeCategory,
  ): Promise<void> => {
    try {
      const response = await abTestingApi.get(
        `/ab-tests/total-annualized-lift/${id}?segments=${segments.join(',')}&testTypeCategory=${testTypeCategory || AbTestTypeCategory.SHIPPING_RATES}`,
      );
      const totalAnnualizedLiftResult = response.data;
      if (totalAnnualizedLiftResult === null) {
        setTotalAnnualizedLift(0);
        return;
      }
      setTotalAnnualizedLift(totalAnnualizedLiftResult[0].annualizedUpliftArpc);
    } catch (error) {
      console.error('Error fetching Total Annualized Lift:', error);
      setTotalAnnualizedLift(null);
    }
  };

  // Fetch free shipping threshold by country code
  const fetchFreeShippingThreshold = async (countryCode: string): Promise<void> => {
    try {
      const response = await abTestingApi.get(`/ab-tests/free-shipping-threshold?countryCode=${countryCode}`);
      setFreeShippingThreshold(response.data);
    } catch (error) {
      console.error('Error fetching Free Shipping Threshold:', error);
      setFreeShippingThreshold(null);
    }
  };

  // Fetch big spender threshold
  const fetchBigSpenderThreshold = async (): Promise<void> => {
    try {
      const response = await abTestingApi.get(`/ab-tests/big-spender`);
      setBigSpenderThreshold(response.data);
    } catch (error) {
      console.error('Error fetching Big Spender Threshold:', error);
      setBigSpenderThreshold(null);
    }
  };

  // Helper function to update winner state locally
  const updateWinnerInState = (variantId: number) => {
    const now = new Date();
    setConcludedAbTests((abTests) =>
      abTests.map((test) => ({
        ...test,
        winnerAt: test.abTestVariants.some((v) => v.id === variantId) ? now : test.winnerAt,
        abTestVariants: test.abTestVariants.map((variant) =>
          variant.id === variantId ? { ...variant, winnerAt: now } : variant,
        ),
      })),
    );
  };

  const abTestActions: IAbTestActions = {
    fetchDraftTests: async () => {
      showFullPageLoader(true);
      await fetchTests(AbTestType.DRAFT, setDraftAbTests);
      showFullPageLoader(false);
    },
    fetchRunningTests: async () => {
      showFullPageLoader(true);
      await fetchTests(AbTestType.RUNNING, setRunningAbTests);
      showFullPageLoader(false);
    },
    fetchConcludedTests: async () => {
      showFullPageLoader(true);
      await fetchTests(AbTestType.CONCLUDED, setConcludedAbTests);
      showFullPageLoader(false);
    },
    upsertTest: async (abTest: AbTest): Promise<number | undefined> => {
      showFullPageLoader(true);
      const response = await abTestingApi.post('/ab-tests/upsert', abTest);
      await abTestActions.fetchDraftTests();
      showFullPageLoader(false);
      return response?.data?.id;
    },
    duplicateTest: async (abTest: AbTest): Promise<void> => {
      showFullPageLoader(true);
      await abTestingApi.post('/ab-tests/upsert', {
        ...abTest,
        id: undefined,
        name: `Copy of - ${abTest.name}`,
        status: AbTestType.DRAFT,
        createdAt: undefined,
        updatedAt: undefined,
        startedAt: undefined,
        endedAt: undefined,
        testComments: undefined,
        winnerAt: undefined,
        testType: abTest.testType,
        abTestVariants: abTest.abTestVariants.map((variant) => ({
          ...variant,
          id: undefined,
          abTestWidgets: variant.abTestWidgets.map((widget) => ({ ...widget, id: undefined })),
        })),
        abTestRules: abTest.abTestRules,
      });
      await abTestActions.fetchDraftTests();
      showFullPageLoader(false);
    },
    startTest: async (id: number): Promise<void> => {
      const testToStart = draftAbTests.find((test) => test.id === id);

      if (testToStart) {
        const testType = testToStart.testType;

        // Site-wide experiments (Cross-Site / Product Page / Smart Cart) are mutually exclusive.
        if (testType && MUTUALLY_EXCLUSIVE_TEST_TYPES.includes(testType)) {
          const runningSameType = runningAbTests.find((test) => test.testType === testType);
          const runningOtherInGroup = runningAbTests.find(
            (test) => test.testType !== testType && MUTUALLY_EXCLUSIVE_TEST_TYPES.includes(test.testType),
          );

          if (runningSameType) {
            showToast({
              message: `Another ${AbTestWidgetTypeLabels[testType]} experiment is currently active. Please end it before starting a new one.`,
              mode: 'danger',
              duration: TimeUnits._SECOND_IN_MS * 10,
            });
            return;
          }

          if (runningOtherInGroup) {
            showToast({
              message: `You cannot start a ${AbTestWidgetTypeLabels[testType]} test while a ${AbTestWidgetTypeLabels[runningOtherInGroup.testType]} test is running`,
              mode: 'danger',
              duration: TimeUnits._SECOND_IN_MS * 10,
            });
            return;
          }
        }
      }

      abTestingApi
        .post(`/ab-tests/${id}/start`)
        .then(() => {
          showToast({
            message: 'Test started',
            mode: 'success',
          });
          fetchTests(AbTestType.RUNNING, setRunningAbTests);
        })
        .catch((error) => {
          fetchTests(AbTestType.DRAFT, setDraftAbTests);
        });
      setDraftAbTests((abTests) => {
        return abTests.filter((x) => x.id !== id);
      });
    },
    endTest: async (id: number): Promise<void> => {
      abTestingApi
        .post(`/ab-tests/${id}/end`)
        .then(() => {
          showToast({
            message: 'Test ended',
            mode: 'success',
          });
        })
        .catch(() => {
          fetchTests(AbTestType.RUNNING, setRunningAbTests);
        });
      setRunningAbTests((abTests) => {
        return abTests.filter((x) => x.id !== id);
      });
    },
    archiveTest: async (id: number): Promise<void> => {
      abTestingApi
        .post(`/ab-tests/${id}/archive`)
        .then(() => {
          showToast({
            message: 'Test archived',
            mode: 'success',
          });
        })
        .catch(() => {
          fetchTests(AbTestType.CONCLUDED, setConcludedAbTests);
        });
      setConcludedAbTests((abTests) => {
        return abTests.filter((x) => x.id !== id);
      });
    },
    deleteTest: async (id: number, type: AbTestType): Promise<void> => {
      abTestingApi.delete(`/ab-tests/${id}`).then(() => {
        showToast({
          message: 'Test deleted',
          mode: 'success',
        });
      });
      if (type == AbTestType.DRAFT) {
        setDraftAbTests((abTests) => {
          return abTests.filter((x) => x.id !== id);
        });
      } else if (type == AbTestType.RUNNING) {
        setRunningAbTests((abTests) => {
          return abTests.filter((x) => x.id !== id);
        });
      } else if (type == AbTestType.CONCLUDED) {
        setConcludedAbTests((abTests) => {
          return abTests.filter((x) => x.id !== id);
        });
      }
    },
    setWinner: async (variantId: number): Promise<string> => {
      showFullPageLoader(true);
      try {
        await noIntercepteroAbTestingApi.post(`/ab-tests/${variantId}/setWinner`);
        showToast({
          message: 'Test ended',
          mode: 'success',
        });
        updateWinnerInState(variantId);
        showFullPageLoader(false);
        return '';
      } catch (error: any) {
        const axiosError = error as AxiosError;
        const response = axiosError.response;
        const responseBody = response.data as any;
        if (response.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
          showFullPageLoader(false);
          return responseBody.message;
        } else {
          console.error('Response error:', responseBody.message, response.status);
          showToast({
            message: `An error has ocurred. Please contact support if the problem persists.`,
            mode: 'danger',
          });
          showFullPageLoader(false);
        }
      }
    },
    setWinnerForce: async (variantId: number): Promise<void> => {
      showFullPageLoader(true);
      await abTestingApi.post(`/ab-tests/${variantId}/setWinner`, { disableLiveCheckoutGroup: true });

      showToast({
        message: 'Test ended',
        mode: 'success',
      });
      updateWinnerInState(variantId);
      showFullPageLoader(false);
    },

    setWinnerPredictedSegmentsV2: async (variantId: number, winnerDto: AbTestWinnerRequestDto): Promise<void> => {
      showFullPageLoader(true);
      try {
        await noIntercepteroAbTestingApi.post(`/ab-tests/${variantId}/v2/set-winner-predicted-segments`, winnerDto);
        showToast({
          message: 'Test ended',
          mode: 'success',
        });
        await fetchTests(AbTestType.CONCLUDED, setConcludedAbTests);
        showFullPageLoader(false);
      } catch (error: any) {
        const axiosError = error as AxiosError;
        const response = axiosError.response;
        const responseBody = response.data as any;
        if (response.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
          showFullPageLoader(false);
          return responseBody.message;
        } else {
          console.error('Response error:', responseBody.message, response.status);
          showToast({
            message: `An error has ocurred. Please contact support if the problem persists.`,
            mode: 'danger',
          });
          showFullPageLoader(false);
        }
      }
    },
    getSegmentsMetrics: fetchSegmentsOrTestVariantMetrics,
    getTestVariantMetrics: fetchSegmentsOrTestVariantMetrics,
    getTotalAnnualizedLift: fetchTotalAnnualizedLift,
  };

  return {
    draftAbTests,
    runningAbTests,
    concludedAbTests,
    abTestActions,
    apiErrorNotification,
    setApiErrorNotification,
    segmentMetrics,
    testVariantMetrics, // New state for test variant metrics
    totalAnnualizedLift,
    freeShippingThreshold,
    bigSpenderThreshold,
    fetchFreeShippingThreshold,
    fetchBigSpenderThreshold,
  };
};

function getErrorTitle(message: string): string {
  if (message.includes('with the same rules is running')) {
    return 'Overlapping rules';
  }

  if (message.includes('test the same set of extensions')) {
    return 'Identical extension groups';
  }

  return '';
}

export default useAbTests;
